import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Space, Table } from 'antd';
import type { TablePaginationConfig } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { VendorContentSubtitle, VendorSeasonListContentRow } from 'type/vendorContentType';
import { DownloadItem, startBatchDownload } from '../download/streamDownload';

const subtitleLabel = (sub: VendorContentSubtitle, idx: number): string => {
  if (typeof sub.lang === 'string' && sub.lang) return sub.lang;
  if (typeof sub.key === 'string' && sub.key) return sub.key;
  return `#${idx + 1}`;
};

const episodeStem = (content: VendorSeasonListContentRow): string => {
  if (content.episode_order != null) return `EP${content.episode_order}`;
  return `content-${content.content_id}`;
};

/**
 * URL의 마지막 path 세그먼트에서 확장자 추출.
 * - 쿼리스트링(signed URL) 무시
 * - 알파벳/숫자 1~6자만 허용
 * - 실패 시 fallback
 */
const extractExtension = (url: string, fallback: string): string => {
  try {
    const u = new URL(url);
    const lastSeg = u.pathname.split('/').filter(Boolean).pop() || '';
    const dotIdx = lastSeg.lastIndexOf('.');
    if (dotIdx <= 0) return fallback;
    const ext = lastSeg.slice(dotIdx + 1);
    if (!/^[a-zA-Z0-9]{1,6}$/.test(ext)) return fallback;
    return ext.toLowerCase();
  } catch (_e) {
    return fallback;
  }
};

const validSubtitles = (
  content: VendorSeasonListContentRow
): Array<{ sub: VendorContentSubtitle; idx: number; url: string }> => {
  const list: Array<{ sub: VendorContentSubtitle; idx: number; url: string }> = [];
  (content.subtitles ?? []).forEach((sub, idx) => {
    if (typeof sub.url === 'string' && sub.url) {
      list.push({ sub, idx, url: sub.url });
    }
  });
  return list;
};

export const countDownloadableFiles = (content: VendorSeasonListContentRow): number => {
  let n = 0;
  if (content.origin_url) n += 1;
  n += validSubtitles(content).length;
  return n;
};

const buildVideoItem = (content: VendorSeasonListContentRow): DownloadItem | null => {
  if (!content.origin_url) return null;
  const stem = episodeStem(content);
  const ext = extractExtension(content.origin_url, 'mp4');
  return {
    url: content.origin_url,
    filename: `${stem}.${ext}`,
    label: `${stem} • Video`,
  };
};

const buildSubtitleItem = (
  content: VendorSeasonListContentRow,
  sub: VendorContentSubtitle,
  idx: number
): DownloadItem | null => {
  if (typeof sub.url !== 'string') return null;
  const stem = episodeStem(content);
  const lang = subtitleLabel(sub, idx);
  const ext = extractExtension(sub.url, 'srt');
  return {
    url: sub.url,
    filename: `${stem}.${lang}.${ext}`,
    label: `${stem} • SRT ${lang.toUpperCase()}`,
  };
};

const buildEpisodeItems = (content: VendorSeasonListContentRow): DownloadItem[] => {
  const items: DownloadItem[] = [];
  const video = buildVideoItem(content);
  if (video) items.push(video);
  validSubtitles(content).forEach(({ sub, idx }) => {
    const it = buildSubtitleItem(content, sub, idx);
    if (it) items.push(it);
  });
  return items;
};

export const buildSeasonItems = (rows: VendorSeasonListContentRow[]): DownloadItem[] => {
  const all: DownloadItem[] = [];
  rows.forEach(row => all.push(...buildEpisodeItems(row)));
  return all;
};

const uploadDate = (content: VendorSeasonListContentRow): string | null => {
  return content.file_uploaded_at ?? null;
};

interface ActionsProps {
  content: VendorSeasonListContentRow;
}

const EpisodeDownloadActions: React.FC<ActionsProps> = ({ content }) => {
  const subs = validSubtitles(content);
  const hasAny = countDownloadableFiles(content) > 0;
  const hasVideo = !!content.origin_url;

  const downloadVideo = () => {
    const item = buildVideoItem(content);
    if (item) startBatchDownload([item]);
  };

  const downloadSingleSubtitle = (sub: VendorContentSubtitle, idx: number) => {
    const item = buildSubtitleItem(content, sub, idx);
    if (item) startBatchDownload([item]);
  };

  const downloadAll = () => {
    startBatchDownload(buildEpisodeItems(content));
  };

  // Subtitle: 0개 disable, 1개 즉시 다운, 여러 개 dropdown.
  let subtitleButton: React.ReactNode;
  if (subs.length === 0) {
    subtitleButton = (
      <Button size="small" disabled>
        Subtitle
      </Button>
    );
  } else if (subs.length === 1) {
    const only = subs[0];
    subtitleButton = (
      <Button size="small" onClick={() => downloadSingleSubtitle(only.sub, only.idx)}>
        Subtitle
      </Button>
    );
  } else {
    const items: MenuProps['items'] = subs.map(({ sub, idx }) => ({
      key: `sub-${idx}`,
      label: subtitleLabel(sub, idx).toUpperCase(),
      onClick: () => downloadSingleSubtitle(sub, idx),
    }));
    subtitleButton = (
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button size="small">
          <Space size={4}>
            Subtitle
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    );
  }

  return (
    <Space wrap>
      <Button size="small" disabled={!hasVideo} onClick={downloadVideo}>
        Video
      </Button>
      {subtitleButton}
      <Button size="small" type="primary" disabled={!hasAny} onClick={downloadAll}>
        All
      </Button>
    </Space>
  );
};

export interface EpisodeDownloadTableProps {
  rows: VendorSeasonListContentRow[];
  loading?: boolean;
  pagination?: TablePaginationConfig | false;
}

export const EpisodeDownloadTable: React.FC<EpisodeDownloadTableProps> = ({
  rows,
  loading,
  pagination = false,
}) => {
  return (
    <Table<VendorSeasonListContentRow>
      rowKey="content_id"
      dataSource={rows}
      loading={loading}
      pagination={pagination}
      columns={[
        {
          title: 'EP',
          dataIndex: 'episode_order',
          width: 70,
          render: (v: number | null | undefined) => (v == null ? '-' : v),
        },
        {
          title: 'Title',
          dataIndex: 'content_title',
          ellipsis: true,
        },
        {
          title: 'Uploaded',
          key: 'uploaded',
          width: 140,
          render: (_, row) => {
            const d = uploadDate(row);
            return d ? dayjs(d).format('YYYY-MM-DD') : '-';
          },
        },
        {
          title: 'Source',
          dataIndex: 'ingest_source',
          key: 'ingest_source',
          width: 90,
          align: 'center',
          render: (v: number | null | undefined) => (v == null ? '-' : v),
        },
        {
          title: 'Download',
          key: 'actions',
          width: 320,
          render: (_, row) => <EpisodeDownloadActions content={row} />,
        },
      ]}
    />
  );
};
